// src/components/lead/sections/LocationSourceCard.tsx
import type React from "react";
import type { ExtractedLeadV2 } from "../../../types/lead";
import type { UpdateLeadFieldFn } from "../../../types/leadActions";
import { AddressAutocompleteInput } from "@/components/common/AddressAutocompleteInput";
import type { AddressAutocompletePick } from "@/lib/addressAutocompleteClient";

type Props = {
  e: ExtractedLeadV2;
  updateCurrentLeadField: UpdateLeadFieldFn;
  locationDisplay: string;
  leadSourceDisplay: string;
  timelineDisplay: string;
  installAddressDisplay: string;
  showLocationDetails: boolean;
  setShowLocationDetails: React.Dispatch<React.SetStateAction<boolean>>;
};

function applyStructuredInstallFields(
  updateCurrentLeadField: UpdateLeadFieldFn,
  pick: AddressAutocompletePick,
) {
  const n = pick.normalized;
  if (n.street) updateCurrentLeadField("install_address_street", n.street);
  if (n.city) updateCurrentLeadField("install_address_city", n.city);
  if (n.state) updateCurrentLeadField("install_address_state", n.state);
  if (n.postal_code) updateCurrentLeadField("install_address_postal_code", n.postal_code);
  const cityState = [n.city, n.state].filter(Boolean).join(", ");
  if (cityState) updateCurrentLeadField("customer_city_state", cityState);
}

export function LocationSourceCard(props: Props) {
  const {
    e,
    updateCurrentLeadField,
    locationDisplay,
    leadSourceDisplay,
    timelineDisplay,
    installAddressDisplay,
    showLocationDetails,
    setShowLocationDetails,
  } = props;

  const installValue = e.install_address_full || installAddressDisplay || "";

  return (
    <div data-app-panel="LocationSourceCard" className="bg-gray-50 rounded-2xl p-4 border border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-xl font-semibold">Location & Source</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {locationDisplay !== "—" ? locationDisplay : "No lot set"} •{" "}
            {leadSourceDisplay !== "—"
              ? `Source: ${leadSourceDisplay}`
              : "Source not set"}{" "}
            • {e.customer_city_state || "City/state not set"}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowLocationDetails((prev) => !prev)}
          className="text-xs text-blue-600 underline"
        >
          {showLocationDetails ? "Hide details" : "Show details"}
        </button>
      </div>

      {showLocationDetails && (
        <>
          <div className="grid grid-cols-1 gap-3 mt-3">
            <div>
              <div className="text-gray-500 text-xs uppercase tracking-wide">Sales Location</div>
              <div className="font-semibold text-sm">{locationDisplay}</div>
            </div>

            <div>
              <div className="text-gray-500 text-xs uppercase tracking-wide mb-1">
                Customer City / State
              </div>
              <input
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="e.g., Moncks Corner, SC"
                value={e.customer_city_state || ""}
                onChange={(ev) =>
                  updateCurrentLeadField("customer_city_state", ev.target.value)
                }
              />
            </div>

            <div>
              <div className="text-gray-500 text-xs uppercase tracking-wide mb-1">
                Install Address (full)
              </div>
              <AddressAutocompleteInput
                value={installValue}
                onChange={(next) => updateCurrentLeadField("install_address_full", next)}
                onPick={(pick) => {
                  updateCurrentLeadField("install_address_full", pick.formatted);
                  applyStructuredInstallFields(updateCurrentLeadField, pick);
                }}
                placeholder="Start typing install address…"
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>

            <div>
              <div className="text-gray-500 text-xs uppercase tracking-wide mb-1">
                Billing Address
              </div>
              <AddressAutocompleteInput
                value={e.billing_address || ""}
                onChange={(next) => updateCurrentLeadField("billing_address", next)}
                onPick={(pick) => updateCurrentLeadField("billing_address", pick.formatted)}
                placeholder="Start typing billing address…"
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="text-gray-500 text-xs uppercase tracking-wide mb-1">
                  Lead Source Category
                </div>
                <select
                  data-lead-field="lead_source_category"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={e.lead_source_category || ""}
                  onChange={(ev) =>
                    updateCurrentLeadField(
                      "lead_source_category",
                      (ev.target.value || null) as ExtractedLeadV2["lead_source_category"],
                    )
                  }
                >
                  <option value="">Select…</option>
                  <option value="facebook">Facebook</option>
                  <option value="google">Google</option>
                  <option value="shedsforsale_com">ShedsForSale.com</option>
                  <option value="drive_by">Drive-by</option>
                  <option value="word_of_mouth">Word of mouth</option>
                  <option value="referral">Referral</option>
                  <option value="bdc_call">BDC call</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <div className="text-gray-500 text-xs uppercase tracking-wide mb-1">
                  Lead Source Detail
                </div>
                <input
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="e.g., Facebook Marketplace ad"
                  value={e.lead_source_detail || ""}
                  onChange={(ev) =>
                    updateCurrentLeadField("lead_source_detail", ev.target.value)
                  }
                />
              </div>
            </div>

            <div>
              <div className="text-gray-500 text-xs uppercase tracking-wide mb-1">
                Distance (from lot / plant)
              </div>
              <div className="font-semibold text-xs text-gray-500">
                {e.distance_from_lot_miles != null || e.distance_from_plant_miles != null
                  ? `${e.distance_from_lot_miles ?? "—"} mi from lot • ${
                      e.distance_from_plant_miles ?? "—"
                    } mi from plant`
                  : "Coming soon"}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-gray-500 text-xs uppercase tracking-wide">
                  Lead Source (summary)
                </div>
                <div className="font-semibold">{leadSourceDisplay}</div>
              </div>

              <div>
                <div className="text-gray-500 text-xs uppercase tracking-wide">
                  Ready To Buy (summary)
                </div>
                <div className="font-semibold">{timelineDisplay}</div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
